import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { NavItem } from '@/hooks/useSidebarOrder';
import { cn } from '@/lib/utils';

interface SortableNavItemProps {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  isDragging?: boolean;
}

const SortableNavItem = ({ item, isActive, onClick, isDragging }: SortableNavItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const IconComponent = item.icon;

  return (
    <SidebarMenuItem
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/nav-item',
        isSortableDragging && 'opacity-50'
      )}
    >
      <SidebarMenuButton
        onClick={onClick}
        isActive={isActive}
        tooltip={item.label}
        className="hover:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-primary data-[active=true]:font-medium relative"
      >
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/nav-item:opacity-50 hover:!opacity-100 cursor-grab active:cursor-grabbing p-1 -ml-1"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </div>
        <IconComponent className="h-4 w-4 shrink-0 ml-3" />
        <span className="group-data-[collapsible=icon]:hidden ml-3">{item.label}</span>
        {item.badge !== undefined && item.badge > 0 && (
          <span className="ml-auto bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full text-xs group-data-[collapsible=icon]:hidden">
            {item.badge}
          </span>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const DragOverlayItem = ({ item }: { item: NavItem }) => {
  const IconComponent = item.icon;
  
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-background border rounded-md shadow-lg">
      <GripVertical className="h-3 w-3 text-muted-foreground" />
      <IconComponent className="h-4 w-4 shrink-0" />
      <span>{item.label}</span>
    </div>
  );
};

interface DraggableSidebarNavProps {
  items: NavItem[];
  activeView: string;
  onViewChange: (view: string) => void;
  onReorder: (activeId: string, overId: string) => void;
}

export const DraggableSidebarNav = ({
  items,
  activeView,
  onViewChange,
  onReorder,
}: DraggableSidebarNavProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const { isMobile, setOpenMobile } = useSidebar();
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      onReorder(active.id as string, over.id as string);
    }
    
    setActiveId(null);
  };

  const activeItem = activeId ? items.find(item => item.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <SidebarMenu>
          {items.map((item) => (
            <SortableNavItem
              key={item.id}
              item={item}
              isActive={activeView === item.view}
              onClick={() => {
                onViewChange(item.view);
                if (isMobile) setOpenMobile(false);
              }}
            />
          ))}
        </SidebarMenu>
      </SortableContext>
      <DragOverlay>
        {activeItem ? <DragOverlayItem item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
